import type { Track } from "~/types/audio"
import { getFullscreenLink, isRouteFullscreen } from "~/utilities/urls"
import type { TextToSpeechOptions } from "~/types/textToSpeech"
import { isTextToSpeechExcluded, mapLanguageToVoice } from "~/utilities/textToSpeech"

export const useAudioStore = defineStore("audio", () => {
  const personalizationStore = usePersonalizationStore()

  const { activeVariant } = storeToRefs(useProductVariantsStore())

  const { articles, activeArticle, activeSubject } = storeToRefs(useProductContentStore())

  const site = useSiteConfig()

  const voice = computed(() => mapLanguageToVoice("nb_NO"))

  const ttsVoice = ref<TextToSpeechOptions["voice"]>(voice.value)

  const { $parseStringToDocument } = useNuxtApp()

  const client = useOidcClient()

  const createSourceUrl = useTTSSourceUrl()

  const playlist = computed(() => {
    return articles.value
      .filter(article => !isTextToSpeechExcluded(article.template))
      .map<Track>(article => {
        const hasAudio = Boolean(article.audio?.metadata?.elementURI)

        const commonTrackData: Track = {
          id: article.id,
          title: article.audio?.title ?? article.title,
          url: article.audio?.metadata?.elementURI,
          duration: article.audio?.metadata?.duration,
          img: article.img?.src,
          textToSpeech: !hasAudio,
          loadTTSOptions: undefined,
          frontendHref: article.frontendHref,
        }

        if (!hasAudio) {
          const key = [
            article.locationId,
            article.versionNo,
            "nb_NO",
            ttsVoice.value,
            site.env !== "production" ? site.env : "",
          ].filter(Boolean).join("-")

          const rawContent = article.body?.html ? `<h1>${article.title}</h1>${article.body.html}` : undefined

          return {
            ...commonTrackData,
            loadTTSOptions: {
              key,
              title: [article.title, activeVariant.value?.title].filter(Boolean).join(" - "),
              title_enabled: false,
              content: $parseStringToDocument(rawContent, ["tts"]).body.innerHTML,
              language: "nb_NO",
              platform: "kunne",
              voice: ttsVoice.value,
              metadata: {
                product: activeVariant.value?.title,
                chapter: activeSubject.value?.title,
              },
              skip_content_check: true,
              authToken: client.getAccessToken() ?? "",
              source_url: createSourceUrl(article.frontendHref),
            },
          }
        }

        return commonTrackData
      })
  })

  const _currentTrackId = ref<number | null>(null)
  const currentTrack = computed({
    get: () => playlist.value.find(track => track.id === _currentTrackId.value),
    set(value: number | null) {
      _currentTrackId.value = value
    },
  })

  const tts = useTextToSpeech(
    computed(() => currentTrack?.value?.loadTTSOptions),
  )

  const src = computed(() => currentTrack.value?.url ?? tts.value.src ?? "")

  const isAutoplay = ref(false)

  const audio = ref<HTMLAudioElement | null>(null)

  watch(src, newSrc => {
    if (audio.value) {
      // Force garbage collection of old audio element
      audio.value.pause()
      audio.value.src = ""
      audio.value.load()
      if (import.meta.client && "mediaSession" in navigator && navigator.mediaSession) {
        navigator.mediaSession.metadata = null
      }
    }

    if (!newSrc) {
      audio.value = null
      return
    }

    const newAudio = new Audio(newSrc)
    newAudio.autoplay = isAutoplay.value
    audio.value = newAudio
  }, { immediate: true })

  const { currentTime, duration: currentDuration, rate: playbackRate, playing: isPlaying, ended } = useMediaControls(audio)

  const route = useRoute()

  const isFullscreen = computed({
    get: () => isRouteFullscreen(route),
    set: value => navigateTo(getFullscreenLink(route, value), {
      replace: true,
    }),
  })

  /**
   * Toggle the play state of the audio player.
   *
   * If the player is not playing, but it is set to autoplay, it will play if there is an available source.
   * This handles the case where the track as ended, and the user wants to replay it.
   *
   * If the player is playing, it will toggle the autoplay state, which in turn will toggle the play state in the watcher below.
   */
  function togglePlayState() {
    if (!isPlaying.value && isAutoplay.value) {
      isPlaying.value = !!src.value
    } else {
      isAutoplay.value = !isAutoplay.value
    }
  }

  // When isAutoplay (i.e. the user's preference to play or pause) updates, update isPlaying.
  watch(isAutoplay, isAutoplay => {
    isPlaying.value = isAutoplay
  })

  // If the user reloads the page while in fullscreen, the player should be active by default.
  const _activePlayer = ref(isFullscreen.value)
  const activePlayer = computed({
    get: () => !!currentTrack.value && (isFullscreen.value || _activePlayer.value),
    set(value: boolean) {
      _activePlayer.value = value
      isAutoplay.value = value
      if (!value) {
        isFullscreen.value = false
      }
    },
  })

  // Pause track if it ends and the player is minimized, if in fullscreen; play the next track in the playlist.
  watch(ended, hasEnded => {
    if (!hasEnded || !currentTrack.value) {
      return
    }

    if (activeVariant.value?.userHasLicence) {
      personalizationStore.addArticleToReadHistory(currentTrack.value.id, true)
    }

    if (!isFullscreen.value) {
      return
    }

    const nextTrack = playlist.value[playlist.value.indexOf(currentTrack.value) + 1]

    if (nextTrack) {
      navigateTo(getFullscreenLink(nextTrack.frontendHref, true))
    } else {
      currentTrack.value = null
    }
  })

  // When the active article changes, update the current track.
  watch(activeArticle, article => {
    // If the article is not available (i.e the user goes out to the chapter overview), we should not update the current track.
    if (!article) {
      return
    }
    currentTrack.value = article?.id ?? null
  })

  // If the track changes while in fullscreen, we should autoplay it.
  watch(currentTrack, track => {
    if (track && isFullscreen.value) {
      isAutoplay.value = true
    }
  })

  return {
    activePlayer,
    playlist,
    currentDuration: readonly(currentDuration),
    currentTrack,
    currentTime,
    playbackRate,
    isPlaying: readonly(isPlaying),
    tts,
    isFullscreen,
    togglePlayState,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(
    acceptHMRUpdate(useAudioStore, import.meta.hot),
  )
